import { type EffectiveSessionTimeout } from '@heidi/workspace/contracts';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
  INACTIVITY_LOGOUT_SETTINGS_ROUTE,
  MFA_SETUP_SETTINGS_ROUTE,
} from '../../contracts';
import { complianceNotificationDismissAtom } from './dismiss-atoms';
import {
  type ComplianceNotificationFlagGates,
  type PinnedComplianceNotification,
} from './types';
import {
  useInactivityLogoutNotificationEligibility,
  useMfaSetupNotificationEligibility,
} from './useComplianceNotificationEligibility';

/**
 * Every compliance recommendation the user is currently eligible for, read or
 * not. Callers pick the half they render: unread rows are pinned above the
 * list, read rows are merged into it by time.
 *
 * `effectiveSessionTimeout` and `flagGates` are threaded through all four hooks
 * in this file rather than read here. The timeout lives in the app's settings
 * tree — see the note on `useInactivityLogoutNotificationEligibility`. The
 * gates resolve through PostHog, which this package's `./flags` door cannot
 * read. Both reach the two filtered readers and the boolean below because each
 * is a thin wrapper over this one.
 */
export function usePinnedComplianceNotifications(
  effectiveSessionTimeout: EffectiveSessionTimeout | null,
  flagGates: ComplianceNotificationFlagGates
): PinnedComplianceNotification[] {
  const showMfa = useMfaSetupNotificationEligibility(flagGates.mfaSetup);
  const showInactivityLogout = useInactivityLogoutNotificationEligibility(
    effectiveSessionTimeout,
    flagGates.inactivityLogout
  );
  const dismissState = useAtomValue(complianceNotificationDismissAtom);

  return useMemo(() => {
    const notifications: PinnedComplianceNotification[] = [];
    if (showMfa) {
      notifications.push({
        type: 'mfa_setup',
        settingsRoute: MFA_SETUP_SETTINGS_ROUTE,
        isRead: dismissState.mfaSetupRead,
        firstShownAt: dismissState.mfaSetupFirstShownAt,
      });
    }
    if (showInactivityLogout) {
      notifications.push({
        type: 'inactivity_logout',
        settingsRoute: INACTIVITY_LOGOUT_SETTINGS_ROUTE,
        isRead: dismissState.inactivityLogoutRead,
        firstShownAt: dismissState.inactivityLogoutFirstShownAt,
      });
    }
    return notifications;
  }, [dismissState, showInactivityLogout, showMfa]);
}

/**
 * The rows that stay pinned above the list.
 *
 * Reading a recommendation is the signal that it has served its purpose as an
 * interruption, so it stops outranking the user's actual notifications and
 * takes its place among them. It remains in the list until the setting is
 * turned on or the row is deleted.
 */
export function useUnreadPinnedComplianceNotifications(
  effectiveSessionTimeout: EffectiveSessionTimeout | null,
  flagGates: ComplianceNotificationFlagGates
): PinnedComplianceNotification[] {
  const notifications = usePinnedComplianceNotifications(
    effectiveSessionTimeout,
    flagGates
  );
  return useMemo(() => notifications.filter(({ isRead }) => !isRead), [notifications]);
}

/** The rows that have been read and now belong in the time-ordered list. */
export function useReadComplianceNotifications(
  effectiveSessionTimeout: EffectiveSessionTimeout | null,
  flagGates: ComplianceNotificationFlagGates
): PinnedComplianceNotification[] {
  const notifications = usePinnedComplianceNotifications(
    effectiveSessionTimeout,
    flagGates
  );
  return useMemo(() => notifications.filter(({ isRead }) => isRead), [notifications]);
}

/**
 * Whether anything is pinned above the list — which is also what decides
 * whether the panel is worth auto-opening on login. A read recommendation is
 * not: it sits in the list like any other row, so opening the panel for it
 * would interrupt the user with nothing to see.
 */
export function useHasPinnedComplianceNotifications(
  effectiveSessionTimeout: EffectiveSessionTimeout | null,
  flagGates: ComplianceNotificationFlagGates
): boolean {
  return (
    useUnreadPinnedComplianceNotifications(effectiveSessionTimeout, flagGates).length > 0
  );
}
